import { OverviewMetrics } from "../schema/overview";

/**
 * The overview table's columns, grouped the way the header groups them. Each group
 * collapses to a single placeholder cell — with ~18 metric columns the table is wider
 * than any screen, and folding away the channels you are not looking at is what makes it
 * readable.
 */

export type OverviewColumnGroupKey = "details" | "overview" | "linkedin" | "email";

export interface OverviewColumn {
	key: string;
	/** i18n key under `analytics.overview.columns.*` */
	labelKey: string;
	group: OverviewColumnGroupKey;
	/**
	 * How the cell renders. `meta` columns are drawn by the row component itself;
	 * `collapsed` is the stand-in a folded group leaves behind.
	 */
	kind: "meta" | "collapsed" | "count" | "countOfTotal" | "rate";
	/** For `count` / `countOfTotal` / `rate` — which metric to read. */
	field?: keyof OverviewMetrics;
	/** For `countOfTotal` — the count's denominator, shown as "n/total (x%)". */
	totalField?: keyof OverviewMetrics;
	rateField?: keyof OverviewMetrics;
	/** Render the value in the positive (green) tone. */
	positive?: boolean;
}

export interface OverviewColumnGroup {
	key: OverviewColumnGroupKey;
	labelKey: string;
	columns: OverviewColumn[];
}

export const OVERVIEW_COLUMN_GROUPS: OverviewColumnGroup[] = [
	{
		key: "details",
		labelKey: "analytics.overview.groups.details",
		columns: [
			{
				key: "channels",
				labelKey: "analytics.overview.columns.channels",
				group: "details",
				kind: "meta",
			},
			{
				key: "senders",
				labelKey: "analytics.overview.columns.senders",
				group: "details",
				kind: "meta",
			},
			{
				key: "lastActivity",
				labelKey: "analytics.overview.columns.lastActivity",
				group: "details",
				kind: "meta",
			},
		],
	},
	{
		key: "overview",
		labelKey: "analytics.overview.groups.overview",
		columns: [
			{
				key: "contacted",
				labelKey: "analytics.overview.columns.leadsContacted",
				group: "overview",
				kind: "countOfTotal",
				field: "contactedLeads",
				totalField: "assignedLeads",
				rateField: "contactedRate",
			},
			{
				key: "ended",
				labelKey: "analytics.overview.columns.leadsEnded",
				group: "overview",
				kind: "countOfTotal",
				field: "endedCampaignLeads",
				totalField: "assignedLeads",
				rateField: "endedRate",
			},
			{
				key: "replyRate",
				labelKey: "analytics.overview.columns.replyRate",
				group: "overview",
				kind: "rate",
				field: "replyRate",
			},
			{
				key: "positiveRate",
				labelKey: "analytics.overview.columns.positiveRate",
				group: "overview",
				kind: "rate",
				field: "positiveRate",
				positive: true,
			},
			{
				key: "sentiment",
				labelKey: "analytics.overview.columns.sentiment",
				group: "overview",
				kind: "rate",
				field: "sentimentRatio",
			},
			// Cross-channel by nature (it reads the lead's best sentiment), so it sits with the
			// other overall numbers rather than under E-Mail as the mockup drew it.
			{
				key: "ooo",
				labelKey: "analytics.overview.columns.ooo",
				group: "overview",
				kind: "count",
				field: "oooRepliedLeads",
			},
		],
	},
	{
		key: "linkedin",
		labelKey: "analytics.overview.groups.linkedin",
		columns: [
			{
				key: "liAccept",
				labelKey: "analytics.overview.columns.acceptRate",
				group: "linkedin",
				kind: "rate",
				field: "linkedinAcceptRate",
			},
			{
				key: "liReply",
				labelKey: "analytics.overview.columns.replyRate",
				group: "linkedin",
				kind: "rate",
				field: "linkedinReplyRate",
			},
			{
				key: "liPositive",
				labelKey: "analytics.overview.columns.positiveRate",
				group: "linkedin",
				kind: "rate",
				field: "linkedinPositiveRate",
				positive: true,
			},
		],
	},
	{
		key: "email",
		labelKey: "analytics.overview.groups.email",
		columns: [
			{
				key: "emOpen",
				labelKey: "analytics.overview.columns.openRate",
				group: "email",
				kind: "rate",
				field: "emailOpenRate",
			},
			{
				key: "emClick",
				labelKey: "analytics.overview.columns.clickRate",
				group: "email",
				kind: "rate",
				field: "emailClickRate",
			},
			{
				key: "emReply",
				labelKey: "analytics.overview.columns.replyRate",
				group: "email",
				kind: "rate",
				field: "emailReplyRate",
			},
			{
				key: "emPositive",
				labelKey: "analytics.overview.columns.positiveRate",
				group: "email",
				kind: "rate",
				field: "emailPositiveRate",
				positive: true,
			},
			{
				key: "emBounce",
				labelKey: "analytics.overview.columns.bounceRate",
				group: "email",
				kind: "rate",
				field: "emailBounceRate",
			},
		],
	},
];

/** Every column of the groups that are currently expanded, in header order. */
/**
 * The single stand-in cell a folded group leaves behind.
 *
 * A collapsed group still occupies one column in the header, so the body must emit one
 * too — dropping it outright slides every later column one place to the left and the
 * numbers end up under the wrong headings.
 */
export function collapsedColumnOf(group: OverviewColumnGroup): OverviewColumn {
	return {
		key: `${group.key}-collapsed`,
		labelKey: group.labelKey,
		group: group.key,
		kind: "collapsed",
	};
}

export function visibleColumns(collapsed: Set<OverviewColumnGroupKey>): OverviewColumn[] {
	return OVERVIEW_COLUMN_GROUPS.flatMap((group) =>
		collapsed.has(group.key) ? [collapsedColumnOf(group)] : group.columns
	);
}
